var __awaiter = (this && this.__awaiter) || function (thisArg, _arguments, P, generator) {
    function adopt(value) { return value instanceof P ? value : new P(function (resolve) { resolve(value); }); }
    return new (P || (P = Promise))(function (resolve, reject) {
        function fulfilled(value) { try { step(generator.next(value)); } catch (e) { reject(e); } }
        function rejected(value) { try { step(generator["throw"](value)); } catch (e) { reject(e); } }
        function step(result) { result.done ? resolve(result.value) : adopt(result.value).then(fulfilled, rejected); }
        step((generator = generator.apply(thisArg, _arguments || [])).next());
    });
};
/** @jsx jsx */
import { jsx } from '@emotion/react';
import React, { Fragment, useCallback, useMemo, useState } from 'react';
import { RefreshScope, useFetch, useRefresh } from 'react-admin-base';
import { FormattedMessage, useIntl } from 'react-intl';
import Select, { components } from "react-select";
import CreatableSelect from 'react-select/creatable';
function Option(props) {
    return jsx(components.Option, Object.assign({}, props), (props.selectProps.children && props.selectProps.children(props.data)) || (props.data.__isNew__ ? jsx(FormattedMessage, { id: "CREATE_VALUE", values: { text: props.children } }) : props.children));
}
function SingleValue(props) {
    return jsx(components.SingleValue, Object.assign({}, props), (props.selectProps.children && props.selectProps.children(props.data)) || (props.data.__isNew__ ? jsx(FormattedMessage, { id: "CREATE_VALUE", values: { text: props.children } }) : props.children));
}
function MultiValue(props) {
    return jsx(components.MultiValue, Object.assign({}, props), (props.selectProps.children && props.selectProps.children(props.data)) || props.children);
}
function EditOrAddIndicator(props) {
    const { className, cx, getStyles, innerProps, isMulti } = props;
    return (jsx("div", Object.assign({}, innerProps, { className: cx({
            indicator: true,
            'clear-indicator': true
        }, className), css: getStyles('clearIndicator', props), onMouseDown: e => {
            e.stopPropagation();
            e.preventDefault();
            props.selectProps.onAddOrEdit();
        } }),
        jsx("i", { className: "fas " + (props.hasValue && !isMulti ? 'fa-pencil-alt' : 'fa-plus') })));
}
function IndicatorsContainer(props) {
    return jsx(components.IndicatorsContainer, Object.assign({}, props),
        props.selectProps.onAddOrEdit && jsx(React.Fragment, null,
            jsx(EditOrAddIndicator, Object.assign({}, props)),
            jsx(components.IndicatorSeparator, Object.assign({}, props))),
        props.children);
}
function MultiValueRemove(props) {
    return jsx(Fragment, null,
        props.selectProps.onAddOrEdit && jsx(components.MultiValueRemove, Object.assign({}, props, { innerProps: Object.assign(Object.assign({}, props.innerProps), { onClick: () => props.selectProps.onAddOrEdit(props.data) }) }),
            jsx("i", { className: "fas fa-pencil", style: { fontSize: '.75em' } })),
        jsx(components.MultiValueRemove, Object.assign({}, props)));
}
const Components = { Option, SingleValue, MultiValue, IndicatorsContainer, MultiValueRemove };
export default function ApiSelect(props) {
    const { disabled, url, getOptionLabel, getOptionValue, idKey, nameKey, filter, group, onCreateOption, getNewOptionData, isMulti, onChange, value, placeholder, staticOptions } = props;
    const intl = useIntl();
    const [search, setSearch] = useState('');
    const params = useMemo(() => ({ query: search }), [search]);
    const [isMenuOpen, setIsMenuOpen] = useState(false);
    const [hasMenuOpen, setHasMenuOpen] = useState(false);
    const [data, loading, error, update] = useFetch((isMenuOpen && url) || false, params);
    const [creating, setCreating] = useState(false);
    let options = staticOptions || (data && data.data) || data;
    if (group) {
        options = group(options || []);
    }
    const handleCreateOption = useCallback(function (input) {
        return __awaiter(this, void 0, void 0, function* () {
            if (!onCreateOption)
                return null;
            setCreating(true);
            try {
                let option = yield onCreateOption(input);
                if (isMulti) {
                    onChange((value || []).concat([option]));
                }
                else {
                    onChange(option);
                }
            }
            finally {
                setCreating(false);
            }
        });
    }, [onCreateOption, setCreating, isMulti, value]);
    const onMenuOpen = useCallback(function () {
        setIsMenuOpen(true);
        setHasMenuOpen(true);
    }, [setIsMenuOpen, setHasMenuOpen]);
    const onMenuClose = useCallback(function () {
        setIsMenuOpen(false);
    }, [setIsMenuOpen]);
    const Component = onCreateOption ? CreatableSelect : Select;
    return jsx(RefreshScope, { update: update },
        jsx(Component, Object.assign({}, props, { className: 'react-select-container', classNamePrefix: "react-select", onCreateOption: onCreateOption && handleCreateOption, getNewOptionData: (onCreateOption && (getNewOptionData || ((inputValue) => ({ [nameKey || 'name']: inputValue, __isNew__: true })))) || undefined, inputValue: search, onInputChange: a => setSearch(a), components: Components, isLoading: !!loading || creating, getOptionLabel: getOptionLabel || ((row) => row[nameKey || 'name']), getOptionValue: getOptionValue || ((row) => row[idKey || 'id']), isDisabled: !!disabled || creating, isClearable: true, isSearchable: true, placeholder: placeholder || intl.formatMessage({ id: 'SELECT' }), options: !options ? [] : ((filter && options.filter(filter)) || options), onMenuOpen: onMenuOpen, onMenuClose: onMenuClose })));
}
export function CreateSelect(props) {
    const { Component, onChange, value, isMulti, idKey } = props;
    const update = useRefresh();
    const [isOpen, setIsOpen] = useState(false);
    const isOpenId = isOpen === true ? null : isOpen && isOpen[idKey || "id"];
    const onReload = useCallback(function (data) {
        setIsOpen(false);
        data && onChange(isMulti ? isOpenId ? value.map(b => b.id === isOpenId ? data : b) : (value || []).concat([data]) : data);
        update && update();
    }, [value, onChange, isMulti, isOpenId, isOpen, update, setIsOpen]);
    return jsx(Fragment, null,
        isOpen && jsx(Component, { id: isOpenId || (!isMulti && value && value[idKey || "id"]), onReload: onReload }),
        jsx(ApiSelect, Object.assign({}, props, { onAddOrEdit: item => setIsOpen(item || true) })));
}
